import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  CheckCircle2Icon,
  AlertTriangleIcon,
  ClockIcon,
  DollarSignIcon,
  DownloadIcon,
  ArrowRightIcon,
} from 'lucide-react'
import { SeoHelmet } from '../components/SeoHelmet'
import { Footer } from '../components/Footer'
import { ROUTE_META } from '../data/routeMeta'

export function ThreeDoors() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [email, setEmail] = useState('')
  const [downloadUrl, setDownloadUrl] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')
    try {
      const res = await fetch('/api/three-doors/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (data.ok) {
        setStatus('success')
        setIsSubmitted(true)
        setDownloadUrl(data.downloadUrl)
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'generate_lead', { campaign: 'three-doors', source: 'landing-page' })
        }
      } else {
        setStatus('error')
        setErrorMsg(data.error || 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setErrorMsg('Network error. Please try again.')
    }
  }

  const scrollToForm = () => {
    document.getElementById('download-form')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen w-full bg-white font-['Roboto'] text-gray-900 selection:bg-primary selection:text-white">
      <SeoHelmet {...ROUTE_META['/three-doors']} url="/three-doors" pageType="website" />

      {/* Minimal Header - Logo Only */}
      <header className="w-full py-6 px-4 sm:px-6 lg:px-8 flex justify-center sm:justify-start max-w-5xl mx-auto">
        <img src="/images/lettrage_2.svg" alt="greenmango" className="h-8 w-auto" />
      </header>

      <main>
        {/* 1. Hero Section */}
        <section className="w-full pt-12 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-navy font-['Poppins'] leading-tight">
                Is your SME stuck behind a door?
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                85.5% of Singapore SMEs have not adopted AI. The gap is not awareness, it is access. Find out which door is blocking you.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  onClick={scrollToForm}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors shadow-lg hover:shadow-xl"
                >
                  Download the checklist
                </button>
                <p className="text-sm text-gray-500">
                  Free PDF.<br />No spam. Takes 2 minutes.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src="/images/three-doors-hero.jpg"
                alt="Three doors illustration"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
            </motion.div>
          </div>
        </section>

        {/* 2. The Three Doors Section */}
        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 border-y border-gray-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Door 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col"
              >
                <div className="w-12 h-12 bg-red-50 text-red-500 rounded-xl flex items-center justify-center mb-6">
                  <DollarSignIcon className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4 font-['Poppins']">Too expensive</h3>
                <p className="text-gray-600 mb-6 flex-grow">
                  "AI feels like a luxury only large enterprises can afford."
                </p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500 font-medium">
                    Traditional agencies start at six figures and take 12 to 18 months.
                  </p>
                </div>
              </motion.div>

              {/* Door 2 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col"
              >
                <div className="w-12 h-12 bg-amber-50 text-amber-500 rounded-xl flex items-center justify-center mb-6">
                  <AlertTriangleIcon className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4 font-['Poppins']">Too risky</h3>
                <p className="text-gray-600 mb-6 flex-grow">
                  "PDPA compliance, data privacy, shadow AI. The risks feel bigger than the rewards."
                </p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500 font-medium">
                    43% of employees share confidential data with public AI tools without employer knowledge.
                  </p>
                </div>
              </motion.div>

              {/* Door 3 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col"
              >
                <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center mb-6">
                  <ClockIcon className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4 font-['Poppins']">Too slow</h3>
                <p className="text-gray-600 mb-6 flex-grow">
                  "Adoption feels overwhelming. Too many tools, too many steps, no clear starting point."
                </p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500 font-medium">
                    Only 14.5% of Singapore SMEs have adopted AI, vs 67% of large enterprises.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 3. What you get section */}
        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-6 font-['Poppins']">
                What you get
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                A one-page PDF checklist that helps you self-diagnose which of the three doors is blocking your SME from adopting AI. Answer 10 simple questions. Get a clear picture of where you stand.
              </p>
              <ul className="space-y-4">
                {[
                  '10 self-diagnostic questions, 2 to 3 per door',
                  'A scoring guide to identify your primary blocker',
                  'A short "what to do next" section per door',
                  'No jargon, no fluff, designed for busy SME owners',
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle2Icon className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-primary/10 transform rotate-3 rounded-2xl"></div>
              <img
                src="/images/three-doors-checklist-preview.jpg"
                alt="PDF Checklist Preview"
                className="relative z-10 w-full h-auto rounded-2xl shadow-xl border border-gray-200"
              />
            </motion.div>
          </div>
        </section>

        {/* 4. Social proof section */}
        <section className="w-full py-16 px-4 sm:px-6 lg:px-8 bg-navy text-center">
          <div className="max-w-3xl mx-auto">
            <p className="text-xl sm:text-2xl text-white/90 font-medium leading-relaxed font-['Poppins']">
              "Based on data from the IMDA SME Digitalisation Study and PDPA enforcement records."
            </p>
          </div>
        </section>

        {/* 5. Final CTA section */}
        <section id="download-form" className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl p-8 sm:p-12 border border-gray-100 text-center">
            {!isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-4 font-['Poppins']">
                  Download the checklist
                </h2>
                <p className="text-xl text-gray-600 mb-10">
                  Find out which door is blocking your SME. It takes 2 minutes.
                </p>

                <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                  <div className="mb-4">
                    <label htmlFor="email" className="sr-only">Email address</label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setStatus('idle') }}
                      placeholder="Enter your email address"
                      className="w-full px-5 py-4 text-lg rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors text-center"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-4 px-8 text-lg font-bold text-white bg-primary hover:bg-primary-dark disabled:bg-primary/50 rounded-lg transition-colors shadow-md hover:shadow-lg mb-6"
                  >
                    {status === 'loading' ? 'Sending...' : 'Download the checklist'}
                  </button>
                  {status === 'error' && (
                    <p className="text-red-600 text-sm mb-4">{errorMsg}</p>
                  )}
                  <p className="text-xs text-gray-500 leading-relaxed">
                    We will only use your email to send you the checklist and relevant updates. No spam. Unsubscribe anytime.
                  </p>
                </form>
              </motion.div>
            ) : (
              /* 6. Thank you / confirmation */
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-8"
              >
                <div className="w-20 h-20 bg-green-100 text-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2Icon className="w-10 h-10" />
                </div>
                <h2 className="text-3xl font-bold text-navy mb-4 font-['Poppins']">
                  Your checklist is ready.
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Download it below.
                </p>

                <div className="flex flex-col items-center gap-6">
                  <a
                    href={downloadUrl}
                    className="inline-flex items-center text-primary font-bold hover:text-primary-dark transition-colors"
                  >
                    <DownloadIcon className="w-5 h-5 mr-2" />
                    Instant download (PDF)
                  </a>

                  <div className="w-full h-px bg-gray-100 my-2"></div>

                  <div className="bg-blue-50 rounded-xl p-6 w-full text-left">
                    <h4 className="font-bold text-navy mb-2">Want to talk through your results?</h4>
                    <p className="text-gray-600 text-sm mb-4">Book a free 15-minute call with Alexandre to discuss your SME's specific blockers.</p>
                    <a
                      href={process.env.REACT_APP_BOOK_URL || '/contact'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Book a free 15-minute call
                      <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
